import { useEffect, useRef, useState } from 'react'
import Cropper from 'react-easy-crop'

import { cn } from '@repo/utils'

import DropZone from '../DropZone'
import ProgressBar from '../ProgressBar'
import SimpleButton from '../SimpleButton'
import getCroppedImg, { createImage } from './cropImage'

import type { FC, ReactNode } from 'react'
import type { Area } from 'react-easy-crop'

const MIN_ZOOM = 0.3
const MAX_ZOOM = 3.3
const MAX_IMAGE_SIZE_TIMES = 3

interface ImageCropProps {
  cropHeight: number
  cropWidth: number

  allowPaste?: boolean
  children?: ReactNode
  className?: string
  contentClassName?: string
  fillStyle?: string
  onCrop?: (croppedImage: string, croppedImageBlob: Blob) => void
  onCut?: (croppedImage: string, croppedImageBlob: Blob) => void
  onDeselect?: () => void
  onRemove?: () => void
  onSelect?: () => void
  readOnly?: boolean
  src?: string
}

const ImageCrop: FC<ImageCropProps> = ({
  allowPaste = true,
  children,
  className = '',
  contentClassName = '',
  cropHeight,
  cropWidth,
  fillStyle = 'white',
  onCrop = undefined,
  onCut = () => null,
  onDeselect = undefined,
  onRemove = undefined,
  onSelect = () => null,
  readOnly = false,
  src,
}) => {
  const dropzoneEl = useRef<HTMLDivElement>(null)
  const croppedAreaPixelRef = useRef<Area | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [isCropping, setIsCropping] = useState(false)
  const [isWorking, setIsWorking] = useState(false)
  const [croppedImage, setCroppedImage] = useState<null | string>(null)
  const [croppedImageBlob, setCroppedImageBlob] = useState<Blob | null>(null)
  const [imageSrc, setImageSrc] = useState<null | string>(null)
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)

  const aspect = cropWidth / cropHeight
  const shouldRenderImage = !!src || !!croppedImage
  const shouldRenderCropper = !src && !!imageSrc && !croppedImage
  const dropzoneWidth = Number(dropzoneEl.current?.offsetWidth) || 0

  const onChangeCrop = (newCrop: typeof crop) => {
    if (newCrop.x !== crop.x || newCrop.y !== crop.y) {
      setCrop(newCrop)
    }
  }

  const onCancel = () => {
    croppedAreaPixelRef.current = null
    onChangeCrop({ x: 0, y: 0 })
    setCroppedImage(null)
    setCroppedImageBlob(null)
    setImageSrc(null)
    setIsCropping(false)
    setIsWorking(false)
    setRotation(0)
    setZoom(1)

    if (imageSrc && onDeselect) {
      onDeselect()
    }
  }

  const onRemoveImage = () => {
    onCancel()
    if (onRemove) {
      onRemove()
    }
  }

  const onCropImage = () => {
    if (onCrop && croppedImage && croppedImageBlob) {
      onCrop(croppedImage, croppedImageBlob)
    }
  }

  const createReducedImageURL = async (url: string) => {
    try {
      const image = await createImage(url)
      if (image.width > cropWidth * MAX_IMAGE_SIZE_TIMES || image.height > cropHeight * MAX_IMAGE_SIZE_TIMES) {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (ctx) {
          const scale = Math.min(
            (cropWidth * MAX_IMAGE_SIZE_TIMES) / image.width,
            (cropHeight * MAX_IMAGE_SIZE_TIMES) / image.height,
          )
          canvas.width = image.width * scale
          canvas.height = image.height * scale

          ctx.fillStyle = fillStyle
          ctx.fillRect(0, 0, canvas.width, canvas.height)
          ctx.drawImage(image, 0, 0, canvas.width, canvas.height)

          // return canvas.toDataURL('image/jpeg', 1)

          return new Promise<null | string>((resolve) => {
            canvas.toBlob((file) => resolve(file ? URL.createObjectURL(file) : null), 'image/jpeg')
          })
        }
      }

      return url
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
    return null
  }

  const onFileChange = async (files: File[]) => {
    onCancel()

    if (files.length > 0) {
      const file = files[0]

      if (file) {
        const reader = new FileReader()
        reader.addEventListener(
          'load',
          async () => {
            const imageDataUrl = reader.result
            if (imageDataUrl) {
              const smallImageURL = await createReducedImageURL(imageDataUrl.toString())
              setImageSrc(smallImageURL)
            }
          },
          false,
        )
        reader.readAsDataURL(file)
        onSelect()
      }
    }
  }

  const showResult = async () => {
    setIsCropping(true)

    if (imageSrc && croppedAreaPixelRef.current) {
      try {
        const [url, blob] = await getCroppedImg({
          fillStyle,
          imageSrc,
          pixelCrop: croppedAreaPixelRef.current,
          rotation,
        })
        if (url && blob) {
          onCut(url, blob)
          setCroppedImage(url)
          setCroppedImageBlob(blob)
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)
      }
    }
    setIsCropping(false)
  }

  useEffect(() => {
    onCancel()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src])

  return (
    <div className={cn('ImageCrop flex flex-col w-full min-w-24', className)}>
      <div className="grow flex">
        <div className="flex-1 block" ref={dropzoneEl}>
          <div
            className={cn('rounded-inherit overflow-hidden pt-[100%] relative w-full', contentClassName)}
            style={{
              paddingTop: `${Math.abs(100 / aspect)}%`,
            }}
          >
            <DropZone
              allowPaste={allowPaste && (!shouldRenderImage || !!croppedImage)}
              className="border-0 p-0 left-0 top-0 size-full absolute"
              disabled={readOnly}
              multiple={false}
              onDrop={onFileChange}
              useRootActions={!shouldRenderCropper}
            >
              {(shouldRenderCropper && (
                <Cropper
                  // Need this if cropSize changes see https://github.com/ricardo-ch/react-easy-crop
                  key={aspect}
                  aspect={aspect}
                  crop={crop}
                  cropSize={{ height: cropHeight, width: cropWidth }}
                  image={imageSrc}
                  maxZoom={MAX_ZOOM}
                  minZoom={MIN_ZOOM}
                  onCropChange={onChangeCrop}
                  onCropComplete={(_croppedArea, cAP) => {
                    croppedAreaPixelRef.current = cAP
                  }}
                  onRotationChange={setRotation}
                  onZoomChange={setZoom}
                  restrictPosition={false}
                  rotation={rotation}
                  style={{
                    containerStyle: {
                      backgroundColor: 'white',
                      height: cropHeight,
                      width: cropWidth,
                      zoom: cropWidth ? dropzoneWidth / cropWidth : 1,
                    },
                  }}
                  zoom={zoom}
                />
              )) ||
                (shouldRenderImage && (
                  <img alt="Recorte de imagen" className="h-auto w-full" src={croppedImage || src} />
                )) ||
                children}
            </DropZone>
          </div>
        </div>
        <div className="flex items-center justify-center w-5">
          {shouldRenderCropper && (
            <div className="flex items-center justify-center -rotate-90">
              {!isCropping && !isWorking && (
                <input
                  aria-labelledby="Zoom de imagen"
                  max={MAX_ZOOM}
                  min={MIN_ZOOM}
                  onChange={({ target }) => setZoom(Number(target.value) || 1)}
                  step={0.1}
                  style={{ width: Number(dropzoneEl.current?.offsetHeight) || 0 }}
                  type="range"
                  value={zoom}
                />
              )}
            </div>
          )}
        </div>
      </div>
      <div>
        {(isCropping || isWorking || shouldRenderCropper) && (
          <div className="flex items-center justify-center h-5 pr-5">
            {(isCropping || isWorking) && <ProgressBar value={100} />}
            {!isCropping && !isWorking && shouldRenderCropper && (
              <div className="flex items-center justify-center">
                <input
                  aria-labelledby="Rotar la imagen"
                  max={180}
                  min={-180}
                  onChange={({ target }) => setRotation(Number(target.value) || 0)}
                  step={1}
                  style={{ width: dropzoneWidth }}
                  type="range"
                  value={rotation}
                />
              </div>
            )}
          </div>
        )}
        {!readOnly && (
          <div className="flex flex-row-reverse justify-between flex-wrap-reverse">
            {shouldRenderCropper && (
              <>
                <SimpleButton
                  ariaLabel="Cancelar corte"
                  icon="cancel"
                  itemsDirection="rtl"
                  onClick={onCancel}
                  secondary
                >
                  Cancelar
                </SimpleButton>
                <SimpleButton ariaLabel="Cortar imagen" icon="check" onClick={showResult} primary>
                  Cortar
                </SimpleButton>
              </>
            )}
            {shouldRenderImage && (
              <>
                {!croppedImage && !!onRemove && (
                  <SimpleButton
                    ariaLabel="Eliminar Imagen"
                    icon="trash"
                    itemsDirection="rtl"
                    onClick={onRemoveImage}
                    secondary
                  >
                    Eliminar
                  </SimpleButton>
                )}
                {!!croppedImage && (
                  <SimpleButton
                    ariaLabel="Cancelar Selección"
                    icon="cancel"
                    itemsDirection="rtl"
                    onClick={onCancel}
                    secondary
                  >
                    Cancelar
                  </SimpleButton>
                )}
                {!!croppedImage && !!onCrop && (
                  <SimpleButton ariaLabel="Guardar Imagen" icon="save" onClick={onCropImage} primary>
                    Guardar
                  </SimpleButton>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ImageCrop
